import { buildContextoLibro, resolverSaldo } from "./resolverSaldo";

/**
 * Fuente ÚNICA de verdad de la fecha de cobro de una factura SII emitida.
 * (ARQUITECTURA 7.18 / reglas 10.66, 10.68, 10.69.)
 *
 * ─── R2.3 (2026-08-31): getCobroReal es ahora un WRAPPER de `resolverSaldo` ───────────────────
 * La resolución MBF/factoring/cuotas ya NO vive acá: la hace el resolver canónico sobre el libro
 * `aplicaciones_saldo` (fuente única de "cuánto se pagó/cobró", regla 10.190). Este archivo
 * conserva la MISMA firma de salida ({cobrada, fecha, via}) y el mismo puente por venta
 * (`buildCobroPorVenta`) para no tocar a sus consumidores (motor de comisión, getFacturasYaVinculadas).
 * Mapa de vía libro→getCobroReal: banco→'mbf', factoring→'factoring', corte_historico→'corte_historico'.
 *
 * Definición de negocio (histórica; hoy la ejecuta el resolver): una factura está cobrada cuando el
 * cliente efectivamente pagó, por cualquiera de tres caminos, evaluados EN ORDEN (MBF → factoring → cuotas):
 *   1. MBF  — Σ de `movimiento_bancario_facturas.monto` (movimientos de Clay, tabla inmutable
 *             `movimientos_bancarios`) ≥ `monto_total` (tolerancia −0.01). Lógica de
 *             `getFacturasYaVinculadas` Fuente 3.
 *   2. Factoring — la factura tiene `estado_factoring='liquidada'`. El factoring es CON RECURSO:
 *             el adelanto NO cuenta; hasta que el cliente le paga al factoring y la operación se
 *             liquida, el riesgo es nuestro y la factura NO está cobrada.
 *   3. Cuotas — todas las cuotas del plan están no-pendientes (Fuente 2 de getFacturasYaVinculadas).
 *
 * (El fallback histórico por FK directa `movimientos_bancarios.factura_sii_emitida_id` —"Camino 4"—
 * se RETIRÓ en B3 Fase A: el excedente de un factoring, linkeado 1:1 por FK, se leía como cobro
 * directo y volvía liquidable una venta cobrada por factoring. El backfill B1 ya llevó los cobros
 * directos legítimos al puente MBF; los de factoring se resuelven por el Camino 2. Ver historial.)
 *
 * Fecha de cobro real = la fecha del evento que cerró el cobro:
 *   1. MBF        → la fecha del movimiento de Clay que COMPLETÓ el monto (acumulando por fecha asc).
 *                   Se deriva del join MBF → movimientos_bancarios.fecha, NO del `facturafechacobro`
 *                   denormalizado.
 *   2. Factoring  → fecha de liquidación de la operación (fecha_pago_real ?? fecha_cobro_final ??
 *                   fecha_operacion), igual que factoring.js al liquidar.
 *   3. Cuotas     → la fecha de la última cuota cobrada.
 *
 * MODEL-AGNOSTIC: la cobranza se resuelve a NIVEL DE FACTURA SII. MBF no conoce el modelo de venta
 * (sales / sales_contratos / sales_btl / negocios_carteleria) — la cobertura se calcula sobre la
 * factura y su `monto_total`, uniforme para los 4 modelos. El conector factura↔venta (vía
 * `facturas_sii_vinculaciones`: venta_tipo/venta_ref_id/venta_ref_index) solo importa para la fecha
 * ESTIMADA, que resuelve `plazosCobroSII.js` por modelo.
 */

/**
 * Mapa de vía del libro (`resolverSaldo`) → vía histórica de getCobroReal. El resolver etiqueta el
 * cobro por el `via` del último evento del libro (`banco`, `factoring`, `corte_historico`…);
 * getCobroReal históricamente devolvía 'mbf'|'factoring'|'cuotas'. Se preserva el contrato de salida:
 *   - banco          → 'mbf'     (el cobro por Clay/MBF entra al libro con via='banco')
 *   - factoring      → 'factoring'
 *   - corte_historico→ 'corte_historico'  (RIESGO LATENTE: no ∈ VIAS_LIQUIDABLES; hoy NINGUNA venta
 *                       vinculada resuelve por esta vía — ver commit R2.3. Antes de R2.3 estos casos
 *                       no existían en el puente; se deja pasar la etiqueta cruda sin remapear.)
 *   - cuotas/fk_directa → intactos (no se producen hoy).
 */
const mapViaLibro = (via) => (via === "banco" ? "mbf" : via);

/**
 * Construye el contexto de cobro a partir del LIBRO `aplicaciones_saldo` (fuente única, regla 10.190),
 * NO de los insumos crudos (MBF/factoring/cuotas). Reemplaza al viejo indexado por-factura: la
 * resolución la hace `resolverSaldo` sobre el contexto del libro.
 *   - aplicacionesSaldo: filas de `aplicaciones_saldo` (documento_tipo='sii_emitida').
 *   - facturasEmitidas: `facturas_sii_emitidas` (id, monto_total, moneda) — el documento a resolver.
 * @returns {{ ctxLibro: object, docById: Map<number, {tipo, id, monto_total, moneda}> }}
 */
export const buildContextoCobro = ({ aplicacionesSaldo = [], facturasEmitidas = [] } = {}) => {
  const ctxLibro = buildContextoLibro(aplicacionesSaldo);
  const docById = new Map();
  for (const f of facturasEmitidas) {
    docById.set(f.id, {
      tipo: "sii_emitida",
      id: f.id,
      monto_total: Number(f.monto_total || 0),
      moneda: f.moneda ?? null,
    });
  }
  return { ctxLibro, docById };
};

/**
 * Resuelve la cobranza real de una factura SII delegando en el resolver canónico del libro
 * (`resolverSaldo`). Preserva la firma histórica {cobrada, fecha, via}.
 * @param {number} facturaSiiId
 * @param {object} ctx  resultado de buildContextoCobro ({ ctxLibro, docById })
 * @returns {{ cobrada: boolean, fecha: string|null, via: 'mbf'|'factoring'|'corte_historico'|'cuotas'|null }}
 */
export const getCobroReal = (facturaSiiId, ctx) => {
  const NO = { cobrada: false, fecha: null, via: null };
  if (facturaSiiId == null || !ctx?.ctxLibro) return NO;
  const doc = ctx.docById?.get(facturaSiiId) || { tipo: "sii_emitida", id: facturaSiiId, monto_total: 0, moneda: null };
  const rs = resolverSaldo(doc, ctx.ctxLibro);
  if (!rs.saldada) return NO;
  return { cobrada: true, fecha: rs.fecha_saldado ?? null, via: mapViaLibro(rs.via) };
};

/**
 * Mapa de cobro POR VENTA (no por factura SII), para consumidores que trabajan a nivel venta
 * (motor de comisión de edificios: sales / sales_contratos por mes). Recorre las vinculaciones
 * factura↔venta (`facturas_sii_vinculaciones`: venta_tipo/venta_ref_id/venta_ref_index → factura
 * SII) y resuelve getCobroReal de cada factura, keyando por `${venta_tipo}:${venta_ref_id}:${venta_ref_index??""}`.
 * Si una venta tiene varias facturas SII, queda cobrada si ALGUNA lo está (fecha más reciente).
 * @param {Array} vinculaciones  filas de facturas_sii_vinculaciones.
 * @param {object} ctx           resultado de buildContextoCobro.
 * @returns {Map<string, {cobrada, fecha, via}>}
 */
export const buildCobroPorVenta = (vinculaciones, ctx) => {
  const map = new Map();
  for (const v of vinculaciones || []) {
    if (v.venta_tipo == null || v.venta_ref_id == null) continue;
    const key = `${v.venta_tipo}:${v.venta_ref_id}:${v.venta_ref_index ?? ""}`;
    const cobro = getCobroReal(v.factura_sii_emitida_id, ctx);
    const prev = map.get(key);
    if (!prev) {
      map.set(key, cobro);
    } else if (cobro.cobrada && (!prev.cobrada || String(cobro.fecha) > String(prev.fecha))) {
      map.set(key, cobro);
    }
  }
  return map;
};

/**
 * Fecha de cobro ESTIMADA (proyectada). Precedencia: fecha_cobro_estimada (override manual) →
 * plazocobro → default (fecha base + 60 días, mirroring pipelineCobroSII). Recibe los campos YA
 * extraídos de la venta; la extracción por modelo (escalar para sales/btl, `meses[i]` para
 * contratos, folio para carteleria) la resuelve el caller vía `plazosCobroSII.js` —acá no se
 * duplica el recorrido de modelos, solo se aplica la precedencia.
 * @param {{ fecha_cobro_estimada?: string|null, plazocobro?: string|null, fecha_base?: string|null }} campos
 * @returns {string|null}
 */
export const getCobroEstimado = ({ fecha_cobro_estimada, plazocobro, fecha_base } = {}) => {
  if (fecha_cobro_estimada) return fecha_cobro_estimada;
  if (plazocobro) return plazocobro;
  if (fecha_base) {
    const d = new Date(`${String(fecha_base).slice(0, 10)}T00:00:00`);
    if (!Number.isNaN(d.getTime())) {
      d.setDate(d.getDate() + 60);
      return d.toISOString().split("T")[0];
    }
  }
  return null;
};
